import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Image as ImageIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Language as GlobeIcon,
  PhotoLibrary as GalleryIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslationApp } from "../../../lib/i18next";
import UploadFileButton from "../../Buttons/UploadFileButton";
import Button from "../../Buttons/Button";
import { useDispatchApp } from "../../../lib/redux";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  BUSINESS_OFFERING_SERVICES,
  FONT_SIZE_BUTTON,
  IMAGE_FORMATS,
} from "../../../utils/constants";
import {
  MODAL_CREATE_WITH_IA_UPLOAD_IMAGE,
  MODAL_CREATE_WITH_IA_GALLERY_BUTTON,
  MODAL_CREATE_WITH_IA_IMAGES_COUNT,
  MODAL_CREATE_WITH_IA_MAX_IMAGES_MESSAGE,
  MODAL_CREATE_WITH_IA_EXTRACT_URL,
  CONFIGURATION_PANEL_REFERENCE_IMAGE_REQUIREMENTS_TITLE,
  CONFIGURATION_PANEL_WEBSITE_URL_TITLE,
  CONFIGURATION_PANEL_ADD_MULTIPLE_PERSPECTIVES_TIP,
  BUTTON_URL,
  BUTTON_UPLOAD,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_PRODUCTS,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_SERVICES,
  MODAL_CREATE_WITH_IA_REFERENCE_IMAGE_ASSETS_CHIP,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_URL,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_UPLOAD,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_URL,
  MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_UPLOAD,
  CONFIGURATION_PANEL_SERVICE_WEBSITE_URL_TITLE,
} from "../../../i18n/keysTranslations";
import TextField from "../../Form/TextField";
import TooltipModal from "../../Tooltip/TooltipModal";
import ContentReferenceImageRequirements from "../../Tooltip/ContentReferenceImageRequirements";
import { BOX_SHADOW_BUTTON } from "../../../utils/colors";
import {
  getCreateWithIAReferenceImageLimit,
  mergeReferenceImageUrls,
} from "../../../utils/createWithIAReferenceImages";

const ReferenceImageThumb = ({ url, onRemove }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
  }, [url]);

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          aspectRatio: "1",
          bgcolor: "grey.100",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {!isLoaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              zIndex: 1,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        <img
          src={url}
          alt=""
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.2s ease-out",
          }}
        />
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            position: "absolute",
            top: { xs: 2, sm: 4 },
            right: { xs: 2, sm: 4 },
            bgcolor: "black",
            width: { xs: 16, sm: 20 },
            height: { xs: 16, sm: 20 },
            zIndex: 2,
          }}
        >
          <CloseIcon
            sx={{
              fontSize: { xs: 10, sm: 12 },
              color: "white !important",
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

const ReferenceImageRequirementsTooltip = () => {
  const { t } = useTranslationApp();

  return (
    <TooltipModal
      sx={{
        px: 0.75,
        fontSize: "0.9rem",
        filter: "brightness(0.9)",
        flexShrink: 0,
      }}
      title={t(CONFIGURATION_PANEL_REFERENCE_IMAGE_REQUIREMENTS_TITLE)}
      content={<ContentReferenceImageRequirements />}
    />
  );
};

const ActionRowWithTooltip = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      width: "100%",
    }}
  >
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: "100%",
        "& > div": {
          width: "100%",
          display: "flex",
        },
        "& .MuiButton-root": {
          width: "100%",
        },
      }}
    >
      {children}
    </Box>
    <ReferenceImageRequirementsTooltip />
  </Box>
);

const MaxImagesMessage = ({ maxImages, t }) => (
  <Typography
    variant="body2"
    sx={{
      color: "text.secondary",
      fontSize: { xs: 10, sm: 11 },
      mt: 0.5,
      display: "block",
      textAlign: "center",
    }}
  >
    {t(MODAL_CREATE_WITH_IA_MAX_IMAGES_MESSAGE, { maxImages })}
  </Typography>
);

const ReferenceImageSelector = ({
  control,
  errors,
  onExtractFromUrl,
  setValue,
  getValues,
  watch,
  activeTab,
  setActiveTab,
  onOpenGallery,
  businessOffering,
  mediaType,
}) => {
  const { t } = useTranslationApp();
  const [isUploading, setIsUploading] = useState(false);
  const [sourceMode, setSourceMode] = useState("url"); // "url" | "upload"

  const isService = businessOffering === BUSINESS_OFFERING_SERVICES;

  const referenceImageTitleKey = isService
    ? MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_SERVICES
    : MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_TITLE_PRODUCTS;

  const referenceImageDescriptionKey = (() => {
    if (sourceMode === "url") {
      return isService
        ? MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_URL
        : MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_URL;
    }
    return isService
      ? MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_SERVICES_UPLOAD
      : MODAL_CREATE_WITH_IA_CONFIGURATION_REFERENCE_IMAGE_DESCRIPTION_PRODUCTS_UPLOAD;
  })();

  const imagesUrl = watch("imagesUrl");
  const websiteUrl = watch("websiteUrl");
  const maxImages = getCreateWithIAReferenceImageLimit({ mediaType });

  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const onImageUpload = (newImages = []) => {
    const currentImages = getValues("imagesUrl") || [];
    setValue(
      "imagesUrl",
      mergeReferenceImageUrls({
        currentUrls: currentImages,
        newUrls: newImages,
        mediaType,
      })
    );
  };

  const removeUrlFromList = (index) => {
    const currentUrls = getValues("imagesUrl") || [];
    setValue(
      "imagesUrl",
      currentUrls.filter((_, i) => i !== index)
    );
  };

  const onTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Initialize sourceMode - default to "url"
  useEffect(() => {
    const currentWebsiteUrl = getValues("websiteUrl");
    // If there is websiteUrl, keep "url"; otherwise, keep default "url"
    if (currentWebsiteUrl) {
      setSourceMode("url");
    }
    // By default it is already "url" from the initial useState
    // eslint-disable-next-line
  }, []);

  // Update sourceMode when websiteUrl changes externally
  useEffect(() => {
    if (websiteUrl) {
      setSourceMode("url");
    }
  }, [websiteUrl]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Title and tooltip */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <ImageIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(referenceImageTitleKey)}
          </Typography>
          <Chip
            label={t(MODAL_CREATE_WITH_IA_REFERENCE_IMAGE_ASSETS_CHIP)}
            size="small"
            sx={{
              bgcolor: "#E6F0F9",
              color: "#4A76A8",
              fontWeight: 600,
              fontSize: 10,
              height: 22,
              borderRadius: 999,
              "& .MuiChip-label": {
                px: 1,
                py: 0,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          {t(referenceImageDescriptionKey)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Segmented Buttons - No title and no top border */}
        <Box
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            p: 0.8,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.5,
          }}
        >
          <Box
            onClick={() => {
              setSourceMode("url");
            }}
            sx={{
              height: 32,
              px: 1.5,
              borderRadius: 1.5,
              fontSize: 12,
              fontWeight: 500,
              transition: "all 0.2s",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              ...(sourceMode === "url" && {
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: BOX_SHADOW_BUTTON,
              }),
            }}
          >
            <GlobeIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {t(BUTTON_URL)}
            </Typography>
          </Box>
          <Box
            onClick={() => {
              setSourceMode("upload");
              if (activeTab >= 2) {
                setActiveTab(0);
              }
            }}
            sx={{
              height: 32,
              px: 1.5,
              borderRadius: 1.5,
              fontSize: 12,
              fontWeight: 500,
              transition: "all 0.2s",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              ...(sourceMode === "upload" && {
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
              }),
            }}
          >
            <UploadIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {t(BUTTON_UPLOAD)}
            </Typography>
          </Box>
        </Box>

        {/* Single title and description section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Mode-specific content */}
          {sourceMode === "url" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                control={control}
                name="websiteUrl"
                placeholder={
                  t(
                    isService
                      ? CONFIGURATION_PANEL_SERVICE_WEBSITE_URL_TITLE
                      : CONFIGURATION_PANEL_WEBSITE_URL_TITLE
                  ) + "..."
                }
                fullWidth
                variant="filled"
                errors={errors}
                rules={{ required: false }}
              />
              <ActionRowWithTooltip>
                <Button
                  onClick={onExtractFromUrl}
                  color="secondary"
                  size="small"
                  fullWidth
                  disabled={imagesUrl.length >= maxImages}
                  sx={{
                    height: 36,
                    bgcolor: "transparent",
                  }}
                >
                  {t(MODAL_CREATE_WITH_IA_EXTRACT_URL)}
                </Button>
              </ActionRowWithTooltip>
              {imagesUrl.length >= maxImages && (
                <MaxImagesMessage maxImages={maxImages} t={t} />
              )}
            </Box>
          ) : (
            <>
              {/* Tabs only for Upload and Gallery (no URL) */}
              <Tabs
                value={activeTab}
                onChange={onTabChange}
                sx={{
                  minHeight: { xs: 36, sm: 40 },
                  "& .MuiTab-root": {
                    minWidth: { xs: 45, sm: 60 },
                    padding: { xs: "6px 8px", sm: "6px 12px" },
                  },
                }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab
                  icon={<UploadIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                  sx={{
                    minHeight: { xs: 36, sm: 40 },
                    fontSize: { xs: 10, sm: 11 },
                  }}
                />
                <Tab
                  icon={<GalleryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                  sx={{
                    minHeight: { xs: 36, sm: 40 },
                    fontSize: { xs: 10, sm: 11 },
                  }}
                />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <ActionRowWithTooltip>
                    <UploadFileButton
                      id="upload-logo-url"
                      textUpload={MODAL_CREATE_WITH_IA_UPLOAD_IMAGE}
                      colorUpload="secondary"
                      onChange={onImageUpload}
                      pathStorage={`socialMedia/${userID}/`}
                      acceptedFormats={IMAGE_FORMATS}
                      loading={isUploading}
                      onCallbackLoading={(loading) => setIsUploading(loading)}
                      modeUpload="sync"
                      multiple={true}
                      icon={<UploadIcon sx={{ fontSize: "18px !important" }} />}
                      disabled={imagesUrl.length >= maxImages}
                      sx={{
                        fontSize: FONT_SIZE_BUTTON,
                        width: "100%",
                        height: { xs: 28, sm: 30 },
                      }}
                    />
                  </ActionRowWithTooltip>
                  {imagesUrl.length >= maxImages && (
                    <MaxImagesMessage maxImages={maxImages} t={t} />
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <ActionRowWithTooltip>
                    <Button
                      onClick={onOpenGallery}
                      color="secondary"
                      size="small"
                      fullWidth
                      startIcon={
                        <GalleryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                      }
                      disabled={imagesUrl.length >= maxImages}
                      sx={{
                        height: { xs: 28, sm: 30 },
                        fontSize: { xs: 12, sm: 14 },
                      }}
                    >
                      {t(MODAL_CREATE_WITH_IA_GALLERY_BUTTON)}
                    </Button>
                  </ActionRowWithTooltip>
                  {imagesUrl.length >= maxImages && (
                    <MaxImagesMessage maxImages={maxImages} t={t} />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {imagesUrl.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ImageIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(MODAL_CREATE_WITH_IA_IMAGES_COUNT, {
                count: imagesUrl.length,
              })}
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 0.5, sm: 1 }}>
            {imagesUrl.map((url, index) => (
              <Grid item xs={4} sm={6} key={`${url}-${index}`}>
                <ReferenceImageThumb
                  url={url}
                  onRemove={() => removeUrlFromList(index)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {!isService && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: 11,
            fontStyle: "italic",
            lineHeight: 1.5,
          }}
        >
          {t(CONFIGURATION_PANEL_ADD_MULTIPLE_PERSPECTIVES_TIP)}
        </Typography>
      )}
    </Box>
  );
};

export default ReferenceImageSelector;
